import { useCallback } from 'react';
import Cookies from 'js-cookie';
import { mutate } from 'swr';
import { Cart } from 'shared/types/cart';
import {
  AlertCode,
  PrepareCartForProgramPayload,
  PrepareCartPayload,
  PrepareCartResponse,
} from 'shared/types/checkout';
import { sdk } from 'sdk';
import { getAccountId, setAlert, useAppDispatch, useAppSelector } from 'store';
import {
  getProductFromLineItem,
  getProductsFromLineItems,
  makeSDKCallWithRetries,
  sendGTMEvent,
} from 'utils';
import { getLoginRedirectUrl, withCookieValidation } from 'utils/edx-auth';
import { fireCheckoutStarted } from 'utils/gtm/checkout';
import { centsToDollars, sumMoney } from 'utils/price';
import { useClearCart } from '../useClearCart';

export const useLoadCart = () => {
  const dispatch = useAppDispatch();
  const customerId = useAppSelector(getAccountId);

  const { clearCart } = useClearCart();

  // This function is used to load the cart with the given product.
  const loadCart = useCallback(
    async ({
      courseRunKeys,
      discountCode,
      programId,
    }: {
      courseRunKeys?: string[];
      discountCode?: string;
      programId?: string;
    }) => {
      let apiResponse;

      if (programId) {
        const payload: PrepareCartForProgramPayload = {
          discountCode,
          programId,
        };
        apiResponse = await withCookieValidation(
          () =>
            makeSDKCallWithRetries('cart/loadCart', customerId, () =>
              sdk.callAction<PrepareCartResponse>({
                actionName: 'cart/loadCart',
                payload,
              }),
            ),
          true,
          customerId,
        );
      } else {
        const payload: PrepareCartPayload = {
          variant: { skus: courseRunKeys ?? [] },
          discountCode,
        };
        apiResponse = await withCookieValidation(
          () =>
            makeSDKCallWithRetries('cart/loadCart', customerId, () =>
              sdk.callAction<PrepareCartResponse>({
                actionName: 'cart/loadCart',
                payload,
              }),
            ),
          true,
          customerId,
        );
      }

      // This code checks whether there is an unhandled exception
      // or timeout that occurred during an extension call.
      if (apiResponse.isError) {
        return apiResponse;
      }

      // This code checks whether there is a handled exception
      // that occurred during an extension call.
      if (apiResponse.data.isError) {
        return apiResponse.data;
      }

      const { cart, discountErrorCode } = apiResponse.data;
      const {
        cartId,
        discountCodes,
        discountOnTotalPrice,
        lineItems,
        sum,
        discountOnLineItems,
      } = cart;

      Cookies.set('chk_cart_id', cartId, {
        expires: 28,
        sameSite: 'lax',
        secure: true,
      });

      Cookies.set('chk_cart_type', 'C', {
        expires: 28,
        sameSite: 'lax',
        secure: true,
      });

      lineItems.forEach((lineItem) => {
        sendGTMEvent({
          event: 'Product Added',
          properties: {
            track_plan_id: 28,
            trigger_source: 'client-side',
            cart_id: cartId,
            checkout_id: cartId,
            coupon: discountCodes[discountCodes.length - 1]?.code ?? null,
            currency: sum.currencyCode ?? 'USD',
            ...getProductFromLineItem(lineItem),
          },
        });
      });

      const formattedResponse = {
        data: cart,
      };

      if (discountErrorCode) {
        await mutate('/action/cart/getCart');
      } else {
        await mutate('/action/cart/getCart', formattedResponse, {
          optimisticData: formattedResponse,
          revalidate: false,
          populateCache: true,
        });
      }

      if (discountErrorCode) {
        dispatch(
          setAlert({
            code: discountErrorCode,
            value: discountCode,
          }),
        );
      } else if (discountCode) {
        sendGTMEvent({
          event: 'Coupon Applied',
          properties: {
            track_plan_id: 30,
            trigger_source: 'client-side',
            cart_id: cartId,
            checkout_id: cartId,
            coupon_name: discountCodes[discountCodes.length - 1]?.code,
            discount: centsToDollars(
              sumMoney(discountOnTotalPrice, discountOnLineItems),
            ),
            currency: discountOnTotalPrice?.currencyCode ?? 'USD',
            products: getProductsFromLineItems(lineItems),
          },
        });
      }

      return apiResponse;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const loadCartForCourses = useCallback(
    async (courseRunKeys: string[], discountCode?: string) => {
      if (courseRunKeys.length > 0) {
        const result = await loadCart({ courseRunKeys, discountCode });
        if (!result || result.isError) {
          // The sku mentioned below is Merchant Center sku which will be one of the failed course run key

          const itemIdentification = result?.error?.itemIdentification as {
            sku: string;
          };
          const action = result?.error?.action as { sku: string };
          const invalidSku = itemIdentification?.sku || action?.sku;

          console.error(
            `Error loading cart with product(s) for customer: ${customerId} with error code: ${
              (result?.error?.code as string) ?? ''
            } and error message: ${
              result?.error?.message ??
              JSON.stringify(result?.error) ??
              'Unknown'
            } for sku: ${invalidSku ?? courseRunKeys.join(', ')}`,
          );

          if (
            result?.error?.code === 'InvalidInput' &&
            result?.error?.message.startsWith('LineItem error: ')
          ) {
            console.error(
              `Product not found for course_run_key: ${invalidSku} for customer: ${customerId}`,
            );
            dispatch(
              setAlert({
                code: AlertCode.PRODUCT_NOT_FOUND,
                value: invalidSku,
              }),
            );
          } else if (
            (result?.error?.code as string)?.includes('authentication_error') ||
            result?.error?.message?.includes('jwt expired')
          ) {
            // Redirect to login page if the token is expired
            window.location.replace(getLoginRedirectUrl());
          } else if (
            result?.error?.code === AlertCode.PRODUCT_CREDIT_ELIGIBILITY_ERROR
          ) {
            console.error(result?.error?.message);
            await clearCart();
            dispatch(
              setAlert({
                code: AlertCode.PRODUCT_CREDIT_ELIGIBILITY_ERROR,
              }),
            );
          } else if (
            result?.error?.code === AlertCode.PRODUCT_UNAVAILABLE_TO_BUY
          ) {
            console.error(
              `Product(s) with course_run_key(s): ${courseRunKeys.join(
                ', ',
              )} that the customer: ${customerId} is trying to purchase have expired.`,
            );
            dispatch(setAlert({ code: AlertCode.PRODUCT_UNAVAILABLE_TO_BUY }));
          } else if (
            result?.error?.code === AlertCode.PRODUCT_ALREADY_ENROLLED
          ) {
            console.error(result?.error?.message);
            await clearCart();
            dispatch(
              setAlert({
                code: AlertCode.PRODUCT_ALREADY_ENROLLED,
              }),
            );
          } else if (
            result?.error?.code === AlertCode.PRODUCT_ALREADY_ENTITLED
          ) {
            console.error(result?.error?.message);
            await clearCart();
            dispatch(
              setAlert({
                code: AlertCode.PRODUCT_ALREADY_ENTITLED,
              }),
            );
          } else {
            console.error(
              `Error loading cart with course_run_key(s): ${courseRunKeys.join(
                ', ',
              )} for customer: ${customerId}`,
            );
            dispatch(setAlert({ code: AlertCode.CART_LOAD_FAILED }));
          }
        } else {
          // Handle successful cart loading and GTM event
          const { cart } = result.data as { cart: Cart };

          fireCheckoutStarted(cart);
        }
      } else {
        // No products supplied – treat as a no-op. Keep existing cart intact and do not surface an error.
        console.info(
          `No course_run_key supplied; skipping cart preparation for customer: ${customerId}`,
        );
      }
      // caller (e.g., AddToCartTastic) is responsible for navigation.
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId],
  );

  const loadCartForProgram = useCallback(
    async (programId: string, discountCode?: string) => {
      if (programId) {
        const result = await loadCart({ discountCode, programId });
        if (!result || result.isError) {
          console.error(
            `Error loading cart with program ID: ${programId} for customer: ${customerId} with error code: ${
              (result?.error?.code as string) ?? ''
            } and error message: ${
              result?.error?.message ??
              JSON.stringify(result?.error) ??
              'Unknown'
            }`,
          );

          if (
            (result?.error?.code as string)?.includes('authentication_error') ||
            result?.error?.message?.includes('jwt expired')
          ) {
            // Redirect to login page if the token is expired
            window.location.replace(getLoginRedirectUrl());
          } else if (result?.error?.code === AlertCode.ORDER_ALREADY_PLACED) {
            console.error(
              `Customer ${customerId} has already purchased the product for program ID: ${programId}.`,
            );
            dispatch(setAlert({ code: AlertCode.ORDER_ALREADY_PLACED }));
          } else if (result?.error?.code === AlertCode.PRODUCT_NOT_FOUND) {
            console.error(
              `Product not found for program ID: ${programId} for customer: ${customerId}`,
            );
            dispatch(
              setAlert({ code: AlertCode.PRODUCT_NOT_FOUND, value: programId }),
            );
          } else if (
            result?.error?.code === AlertCode.PRODUCT_ALREADY_ENROLLED
          ) {
            console.error(result?.error?.message);
            await clearCart();
            dispatch(
              setAlert({
                code: AlertCode.PRODUCT_ALREADY_ENROLLED,
              }),
            );
          } else if (
            result?.error?.code === AlertCode.PRODUCT_ALREADY_ENTITLED
          ) {
            console.error(result?.error?.message);
            await clearCart();
            dispatch(
              setAlert({
                code: AlertCode.PRODUCT_ALREADY_ENTITLED,
              }),
            );
          } else {
            console.error(
              `Error loading cart with program ID: ${programId} for customer: ${customerId}`,
            );
            dispatch(setAlert({ code: AlertCode.CART_LOAD_FAILED }));
          }
        } else {
          // Handle successful cart loading
          const { cart } = result.data as { cart: Cart };

          fireCheckoutStarted(cart);
        }
      } else {
        // No program ID supplied – treat as a no-op. Keep existing cart intact and do not surface an error.
        console.info(
          `No programId supplied; skipping cart preparation for customer: ${customerId}`,
        );
      }
      // caller will handle navigation.
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId],
  );

  return {
    loadCartForCourses,
    loadCartForProgram,
  } as const;
};
